/**
 * Norwegian Bokmål message catalog — mirrors the shape of
 * `messages.en.ts` exactly; `satisfies MessagesShape` makes a missing or
 * renamed key a TypeScript error. Register: informal du (locked in
 * l10n/locales.md). Uses the `nb` qualifier, never `no`.
 *
 * Platform terms follow Norwegian UI conventions (Tema for theme, samtale
 * for conversation, koble sammen/sammenkobling for pair, avvis for dismiss,
 * mottaker for a receiving surface); "Glance" is the untranslated feature
 * brand name (glossary D1) and "klipp" is the locked D2 loanword in its
 * native form.
 *
 * Plural notes: Norwegian Bokmål is a {one, other} CLDR locale (English-like
 * boundaries), same shape as de/nl. Count nouns inflect (melding → meldinger,
 * enhet → enheter) while neuter monosyllables stay invariant (treff → treff,
 * tegn → tegn, anrop → anrop), so every count-bearing function still selects
 * through `selectPlural("nb", …)`.
 *
 * Out of scope: the same exclusions as the English catalog (log
 * scopes, structured event names, JSDoc, server-emitted error
 * messages — see `messages.en.ts` for the full list).
 */
import { selectPlural } from "@reflecto/client-core";
import type { MessagesShape } from ".";

export const messages = {
  header: {
    banner: "Reflecto",
    statusConnected: "Tilkoblet",
    statusReconnecting: "Kobler til på nytt…",
    statusDisconnected: "Frakoblet",
    openSettings: "Åpne innstillinger",
    pairedFallbackDevice: "telefonen din",
    publicModeToggleEnable: "Skjul varselinnhold",
    publicModeToggleDisable: "Vis varselinnhold",
  },

  notifications: {
    replyAriaLabel: "Svar",
    replyPlaceholder: "Svar…",
    replySend: "Send",
    sent: "Sendt",
    failedToSend: "Sending mislyktes",
    unread: "Ulest",
    dismiss: "Avvis",
    notificationImageAlt: "Varselbilde",
  },

  conversations: {
    replyAriaLabel: "Svar",
    replyPlaceholder: "Svar…",
    replySend: "Send",
    sent: "Sendt",
    failedToSend: "Sending mislyktes",
    dismiss: "Avvis samtale",
    messagesListLabel: "Meldinger i denne samtalen",
    deviceOwner: "Du",
    unreadAriaLabel: (count: number): string =>
      count === 0
        ? "Ingen uleste meldinger"
        : `${count} ${selectPlural("nb", count, {
            one: "ulest melding",
            other: "uleste meldinger",
          })}`,
  },

  list: {
    today: "I dag",
    yesterday: "I går",
    searchNotifications: "Søk i varsler",
    clearSearch: "Tøm søk",
    clearAll: "Fjern alle",
    markAllRead: "Merk alle som lest",
    groupCheckboxLabel: "Grupper",
    noNotificationsTitle: "Ingen varsler ennå",
    noNotificationsDesc: "Nye meldinger speilet fra telefonen din vises her.",
    noMatchesTitle: "Ingen treff",
    notificationsList: "Varsler",
    privacyAppList: "Varsler per app",
    clear: "Fjern",
    clearMatches: (count: number): string =>
      `Fjern ${count} ${selectPlural("nb", count, { one: "treff", other: "treff" })}`,
    noMatchesDesc: (query: string): string => `Ingen varsler samsvarer med «${query}».`,
    appNotificationsGroup: (appName: string): string => `Varsler fra ${appName}`,
    dismissAllFrom: (appName: string): string => `Avvis alle fra ${appName}`,
    privacyCount: (count: number, kind: "messages" | "calls" | "notifications") => {
      const noun =
        kind === "messages"
          ? selectPlural("nb", count, { one: "melding", other: "meldinger" })
          : kind === "calls"
            ? selectPlural("nb", count, { one: "anrop", other: "anrop" })
            : selectPlural("nb", count, { one: "varsel", other: "varsler" });
      return `${count} ${noun}`;
    },
    kbdDismiss: "avvis",
    kbdNavigate: "naviger",
    kbdOrSeparator: "eller",
  },

  settings: {
    back: "Tilbake til varsler",
    settings: "Innstillinger",
    appearance: "Utseende",
    theme: "Tema",
    themeAuto: "Automatisk",
    themeLight: "Lyst",
    themeDark: "Mørkt",
    language: "Språk",
    languageSystemDefault: "Systemstandard",
    notificationsSection: "Varsler",
    osNotifications: "Systemvarsler",
    callNotifications: "Anropsvarsler",
    privacySection: "Personvern",
    analyticsToggle: "Anonym bruksstatistikk",
    analyticsToggleDescription: "Kun antall og milepæler — aldri varslene dine.",
    connection: "Tilkobling",
    status: "Status",
    statusConnected: "Tilkoblet",
    statusReconnecting: "Kobler til på nytt…",
    statusDisconnected: "Frakoblet",
    disconnectFromPhone: "Koble fra telefonen",
    disconnectConfirm: "Er du sikker? Dette fjerner sammenkoblingen av enheten din.",
    yesDisconnect: "Ja, koble fra",
    cancel: "Avbryt",
    aboutAndSupport: "Om og støtte",
    version: "Versjon",
    encryption: "Kryptering",
    encryptionValue: "E2E (NaCl)",
    needHelp: "Trenger du hjelp? Send e-post til __EMAIL__",
    disconnectFailed: "Kunne ikke koble fra — prøv igjen",
    disconnectLocalCleanupFailedWithError: (errorMsg: string): string =>
      `Koblet fra, men lokal opprydding mislyktes: ${errorMsg}. Last inn siden på nytt.`,
    disconnectLocalCleanupFailed:
      "Koblet fra, men lokal opprydding mislyktes. Last inn siden på nytt.",
  },

  pairing: {
    enterCodeInstruction: "Skriv inn denne koden i Reflecto-appen",
    pairedSuccessfully: "Sammenkobling vellykket!",
    pairingFailed: "Sammenkobling mislyktes",
    requestingPairingCode: "Ber om koblingskode…",
    codeExpired: "Koden er utløpt",
    tryAgain: "Prøv igjen",
    failedToStartPairing: "Kunne ikke starte sammenkobling",
    needAppText: "Trenger du Android-appen? Send e-post til __EMAIL__ for tilgang.",
  },

  dnd: {
    title: "Ikke forstyrr",
    description: "Sett varsler på denne enheten på pause den neste timen.",
    snooze1h: "Sett på pause i 1 time",
    snoozeActiveUntil: (formattedTime: string): string => `Aktiv til ${formattedTime}`,
    cancelSnooze: "Avbryt",
  },

  connectors: {
    title: "Tilkoblede tjenester",
    emptyBody:
      "Ingen tilkoblede tjenester ennå. Koble til ChatGPT eller Claude fra koblingsinnstillingene deres, og godkjenn deretter koden på telefonen din.",
    scopeSend: "Kan sende varsler",
    added: (date: string): string => `Lagt til ${date}`,
    lastUsed: (relative: string): string => `Sist brukt ${relative}`,
    neverUsed: "Aldri brukt",
    revoke: "Koble fra",
    revokeConfirm: (label: string): string => `Koble fra ${label}? Den mister tilgang umiddelbart.`,
    revokeAction: "Koble fra",
    revokeConfirmLabel: (label: string): string => `Koble fra ${label}`,
    cancel: "Avbryt",
    loadError: "Kunne ikke laste tilkoblede tjenester. Prøv igjen.",
    revokeError: "Kunne ikke koble fra tjenesten. Prøv igjen.",
    retry: "Prøv igjen",
  },

  feedback: {
    title: "Send tilbakemelding",
    close: "Lukk",
    messageLabel: "Meldingen din",
    messagePlaceholder: "Funnet en feil, har en idé, eller føles noe galt? Fortell oss det.",
    categoryLabel: "Kategori",
    categoryBug: "Feil",
    categoryIdea: "Idé",
    categoryOther: "Annet",
    emailLabel: "E-post (valgfritt, kun hvis du vil ha svar)",
    emailPlaceholder: "deg@eksempel.no",
    emailHelp: "Vi setter deg ikke på noen liste.",
    disclosure:
      "Meldingen din sendes til Reflectos server og registreres i vår interne feilsporing. Vi legger ved appversjonen din og en enhets-ID tildelt under sammenkobling slik at vi kan korrelere rapporter. Vi ser verken varslene, kontaktene eller andre personopplysninger dine. E-post er valgfritt — oppgi den bare hvis du vil ha svar. Vi setter deg ikke på noen liste.",
    submit: "Send tilbakemelding",
    sending: "Sender…",
    success: "Takk — lest av et menneske, vanligvis innen 24 t",
    error: "Kunne ikke sende. Prøv igjen.",
    done: "Ferdig",
    sendFeedback: "Send tilbakemelding",
  },

  glance: {
    title: "Glance",
    appName: "Reflecto",
    closeLabel: "Lukk",
    aiSummaryTag: "AI-sammendrag",
    errorAi: "Kunne ikke lage et sammendrag akkurat nå.",
    retryCta: "Prøv igjen",
    downloadHeading: "Få AI-sammendrag",
    downloadBody:
      "Ta igjen det du har gått glipp av med et AI-sammendrag på enheten. Kjører helt og holdent på enheten din.",
    downloadCta: "Last ned modell",
    downloadPrivacyNote: "En engangsnedlasting. Serveren ser aldri varslene dine.",
    downloadingHeading: "Laster ned modell…",
    caughtUp: "Du er helt oppdatert — ingenting krever oppmerksomheten din akkurat nå.",
    needsYou: "Krever oppmerksomhet",
    quiet: "Annet",
    scanning: (pending: number): string => `Oppsummerer ${pending}…`,
    jump: "Åpne varsel",
    affordanceReply: "Svar",
    affordanceOpen: "Åpne",
    affordanceCopyOtp: "Kopier kode",
    buttonLabel: "Glance",
  },

  update: {
    title: "Oppdatering tilgjengelig",
    description: "En ny versjon av Reflecto er klar. Last inn på nytt for å oppdatere.",
    reload: "Last inn på nytt",
  },

  pairConfirm: {
    cardMessage: (name: string): string => `Vi kalte denne enheten «${name}».`,
    cardLooksGood: "Ser bra ut",
    cardEdit: "Rediger",
    cardAriaLabel: "Bekreft enhetsnavn",
    renameTitle: "Gi denne enheten nytt navn",
    renameLabel: "Enhetsnavn",
    renameSave: "Lagre",
    renameCancel: "Avbryt",
    renameFailed: "Kunne ikke gi nytt navn. Prøv igjen.",
  },

  collision: {
    title: (name: string): string => `Du har allerede en «${name}»`,
    subtitle: "Hva skal vi kalle denne?",
    chipWork: (name: string): string => `Jobb ${name}`,
    chipHome: (name: string): string => `Hjemme ${name}`,
    chipPersonal: (name: string): string => `Privat ${name}`,
    customLabel: "Egendefinert navn",
    save: "Lagre",
    useAnyway: "Bruk det opprinnelige navnet likevel",
    saveFailed: "Kunne ikke lagre det nye navnet. Prøv igjen.",
    ariaLabel: "Løs duplisert enhetsnavn",
  },

  clipboard: {
    title: "Utklippstavle",
    empty: "Klipp fra de andre enhetene dine dukker opp her. Trykk for å kopiere.",
    from: (label: string): string => `fra ${label}`,
    copy: "Kopier til utklippstavle",
    copied: "Kopiert",
    reveal: "Vis",
    hide: "Skjul",
    maskedHint: "Innhold skjult — trykk på Vis for å vise",
    sensitiveChip: "Sensitiv",
    cloudChip: "via API-tokenet ditt",
    sendPlaceholder: "Lim inn tekst for å sende…",
    sendCta: "Send",
    sendSensitiveToggle: "Merk som sensitivt",
    sendSuccess: (n: number): string =>
      `Sendt til ${n} ${selectPlural("nb", n, { one: "enhet", other: "enheter" })}.`,
    sendPartial: (n: number, total: number): string =>
      `Nådde ${n}/${total} ${selectPlural("nb", total, { one: "enhet", other: "enheter" })}.`,
    sendNoDevices: "Ingen sammenkoblede enheter å sende til.",
    sendFailed: "Kunne ikke sende. Prøv igjen.",
    sendEmpty: "Ingenting å sende.",
    dockExpand: "Vis utklippshistorikk",
    dockCollapse: "Skjul",
    dockSendTitle: "Send til enhetene dine",
    dockFromClipboard: (chars: number): string =>
      `Fra utklippstavle · ${chars} ${selectPlural("nb", chars, {
        one: "tegn",
        other: "tegn",
      })}`,
    dockSend: (n: number): string => `Send til ${n}`,
    dockCancel: "Avbryt",
    dockRecentHeading: "Nylig",
    dockEmptyClipboard: "Utklippstavlen din er tom.",
    shareTitle: "Send til enhetene dine",
    shareReview:
      "Se over hva du sender. Det går til de sammenkoblede enhetene dine, ende-til-ende-kryptert.",
    shareSend: "Send",
    shareCancel: "Avbryt",
    shareNotPaired: "Koble sammen en enhet først for å sende delt tekst.",
    shareOpenApp: "Åpne Reflecto",
  },
  install: {
    title: "Installer Reflecto",
    body: "Legg til på startskjermen for raskere tilgang.",
    cta: "Installer",
    dismissAria: "Lukk installasjonsforespørsel",
  },
  common: {
    loading: "Laster",
  },
  errors: {
    notPaired: "Ikke sammenkoblet",
  },
  device: {
    nameTemplate: (browser: string, os: string) => `${browser} på ${os}`,
    webFallback: "Nettleser",
  },

  palette: {
    placeholder: "Skriv en kommando…",
    noResults: "Ingen samsvarende kommandoer",
    ariaLabel: "Kommandopalett",
    clearAll: "Fjern alle varsler",
    glance: "Glance-sammendrag",
    cancelSnooze: "Avbryt pause",
    copyClip: "Kopier siste mottatte klipp",
  },
} satisfies MessagesShape;
